<!-- SensorSharingModal.vue -->
<script setup>
import { ref } from 'vue';
import { useFetch } from "@/plugins/fetch";
import ArgonButton from "@/components/Argon/ArgonButton.vue";
import { useToast } from "primevue/usetoast";
import Dropdown from 'primevue/dropdown';
import Dialog from 'primevue/dialog';
import AutoComplete from 'primevue/autocomplete';

const props = defineProps({
  visible: Boolean,
  sensorId: {
    type: String,
    required: true
  }
})
const emit = defineEmits(['update:visible', 'shared']);

const fetch = useFetch();
const toast = useToast();

// const searchQuery = ref('');
const selectedUser = ref(null);
const filteredUsers = ref([]);
const accessLevel = ref('READ');
const isLoading = ref(false);

const accessLevels = [
  { label: 'Read Only', value: 'READ' },
  { label: 'Write Access', value: 'WRITE' },
  { label: 'Admin Access', value: 'ADMIN' }
];

const searchUsers = async (event) => {
  try {
    const response = await fetch('users/search?email=' + event.query, {
      method: 'GET',
    });
    filteredUsers.value = response;
  } catch (error) {
    console.error('Error searching users:', error);
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to search users', life: 3000 });
  }
};

const shareSensor = async () => {
  if (!selectedUser.value) {
    toast.add({ severity: 'warn', summary: 'Warning', detail: 'Please select a user', life: 3000 });
    return;
  }

  isLoading.value = true;
  try {
    await fetch(`sensors/${props.sensorId}/share`, {
      method: 'POST',
      body: {
        user_email: selectedUser.value.email,
        access_level: accessLevel.value
      }
    });

    toast.add({ severity: 'success', summary: 'Success', detail: 'Sensor shared successfully', life: 3000 });
    emit('shared');
    closeModal();
  } catch (error) {
    console.error('Error sharing sensor:', error);
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to share sensor', life: 3000 });
  } finally {
    isLoading.value = false;
  }
};

const closeModal = () => {
  selectedUser.value = null;
  accessLevel.value = 'READ';
  emit('update:visible', false);
};
</script>

<template>
  <Dialog
      :visible="visible"
      @update:visible="(val) => emit('update:visible', val)"
      header="Share Sensor"
      :modal="true"
      :closable="true"
      @hide="closeModal"
      class="p-fluid"
  >
    <div class="p-field mb-3">
      <label for="user" class="form-label">Share with User</label>
      <AutoComplete
          v-model="selectedUser"
          :suggestions="filteredUsers"
          @complete="searchUsers"
          field="email"
          placeholder="Search by email"
          :disabled="isLoading"
      />
    </div>

    <div class="p-field mb-3">
      <label for="accessLevel" class="form-label">Access Level</label>
      <Dropdown
          v-model="accessLevel"
          :options="accessLevels"
          optionLabel="label"
          optionValue="value"
          placeholder="Select Access Level"
          :disabled="isLoading"
      />
    </div>

    <template #footer>
      <ArgonButton
          type="button"
          class="btn btn-light"
          @click="closeModal"
          :disabled="isLoading"
      >
        Cancel
      </ArgonButton>
      <ArgonButton
          type="button"
          class="btn bg-gradient-success ms-2"
          @click="shareSensor"
          :disabled="isLoading"
      >
        <i v-if="isLoading" class="fas fa-spinner fa-spin me-2"></i>
        Share
      </ArgonButton>
    </template>
  </Dialog>
</template>